/**
 * Adjust the brightness of a color
 * @param color - CSS color string (hex, rgb, hsl, etc.)
 * @param brightness - Brightness percentage (0-100, where 100 is original brightness)
 * @returns Adjusted color string
 */
export function adjustColorBrightness(
  color: string,
  brightness: number
): string {
  // Convert brightness from 0-100 to 0-1
  const brightnessFactor = brightness / 100;

  // Create a temporary element to use the browser's color parsing
  const tempElement = document.createElement("div");
  tempElement.style.color = color;
  document.body.appendChild(tempElement);

  // Get computed color
  const computedColor = getComputedStyle(tempElement).color;
  document.body.removeChild(tempElement);

  // Parse RGB values
  const rgbMatch = computedColor.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (!rgbMatch) return color;

  const r = parseInt(rgbMatch[1]);
  const g = parseInt(rgbMatch[2]);
  const b = parseInt(rgbMatch[3]);
  const a = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;

  // Adjust brightness
  const adjustedR = Math.round(r * brightnessFactor);
  const adjustedG = Math.round(g * brightnessFactor);
  const adjustedB = Math.round(b * brightnessFactor);

  // Return as rgba
  return `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${a})`;
}

/**
 * Adjust the saturation of a color
 * @param color - CSS color string (hex, rgb, hsl, etc.)
 * @param saturation - Saturation percentage (0-100, where 100 is original saturation)
 * @returns Adjusted color string
 */
export function adjustColorSaturation(
  color: string,
  saturation: number
): string {
  // Convert saturation from 0-100 to 0-1
  const saturationFactor = saturation / 100;

  // Create a temporary element to use the browser's color parsing
  const tempElement = document.createElement("div");
  tempElement.style.color = color;
  document.body.appendChild(tempElement);

  // Get computed color
  const computedColor = getComputedStyle(tempElement).color;
  document.body.removeChild(tempElement);

  // Parse RGB values
  const rgbMatch = computedColor.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (!rgbMatch) return color;

  const r = parseInt(rgbMatch[1]);
  const g = parseInt(rgbMatch[2]);
  const b = parseInt(rgbMatch[3]);
  const a = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;

  // Convert RGB to HSL for saturation adjustment
  const [h, s, l] = rgbToHsl(r, g, b);

  // Adjust saturation
  const adjustedS = s * saturationFactor;

  // Convert back to RGB
  const [adjustedR, adjustedG, adjustedB] = hslToRgb(
    h,
    adjustedS * 100,
    l * 100
  );

  // Return as rgba
  return `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${a})`;
}

/**
 * Adjust both brightness and saturation of a color
 * @param color - CSS color string (hex, rgb, hsl, etc.)
 * @param brightness - Brightness percentage (0-100)
 * @param saturation - Saturation percentage (0-100)
 * @returns Adjusted color string
 */
export function adjustColor(
  color: string,
  brightness: number,
  saturation: number
): string {
  // First adjust saturation
  const saturationAdjusted = adjustColorSaturation(color, saturation);
  // Then adjust brightness
  return adjustColorBrightness(saturationAdjusted, brightness);
}

/**
 * Convert RGB to HSL
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns HSL values as array [h, s, l] where h is 0-360, s and l are 0-1
 */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s, l];
}

export function hslToRgb(
  h: number,
  s: number,
  l: number
): [number, number, number] {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}
